'use client'

import React from 'react'
import { ExternalLink, History } from 'lucide-react'
import { cn } from '@/lib/utils'
import { ESPO_URL } from '@/utils/constants'
import { Skeleton } from '@/components/ui/skeleton'
import { BTC_ID, formatAmount } from '../lib/amm'
import { numCompact } from '../lib/format'
import { useAlkanes } from '../lib/store'
import useHistory, { type HistoryLeg, type HistoryRow } from '../lib/useHistory'

const FRBTC_ID = '32:0'
const BTC_ICON = '/market/btc-collection.svg'

interface TokenMeta {
	name: string
	icon: string | null
}

/// One history surface for every place the app shows what a wallet did:
/// the profile, the trading screen's "My history" tab, the phone Activity
/// tab. Same rows, same vocabulary, same pending treatment — a broadcast
/// appears here the instant this browser signs it, turns into a mempool row
/// once relayed, and settles into the ledger's exact row when it confirms.
///
/// Row anatomy is fomo's: the ASSET leads (icon + name), the verb and time
/// are the subline, and the signed amount sits in a right-aligned tabular
/// column with the counter-leg beneath it. The explorer link lives outside
/// the row's click target — no control nested inside a control.
export default function HistoryList({
	address,
	own,
	compact = false,
	limit,
	kinds
}: {
	address: string | null
	/// The connected wallet's own address: merges this browser's broadcast
	/// records and words the empty state in the first person.
	own: boolean
	compact?: boolean
	limit?: number
	/// Only these kinds (a Buys/Sells filter); everything when absent.
	kinds?: HistoryRow['kind'][]
}) {
	const assets = useAlkanes((s) => s.assets)
	const pools = useAlkanes((s) => s.pools)
	const navigate = useAlkanes((s) => s.navigate)
	const history = useHistory(address, own)

	const meta = React.useMemo(() => {
		const m = new Map<string, TokenMeta>()
		m.set(BTC_ID, { name: 'BTC', icon: BTC_ICON })
		for (const p of pools) {
			for (const t of [p.token0, p.token1]) {
				if (!m.has(t.id) || !m.get(t.id)?.icon) {
					m.set(t.id, { name: t.name || t.id, icon: t.icon ?? null })
				}
			}
		}
		for (const a of assets) {
			const prev = m.get(a.id)
			m.set(a.id, { name: a.name || prev?.name || a.id, icon: a.icon ?? prev?.icon ?? null })
		}
		if (!m.has(FRBTC_ID)) {
			m.set(FRBTC_ID, { name: 'frBTC', icon: null })
		}
		return m
	}, [assets, pools])
	const metaOf = React.useCallback(
		(id: string): TokenMeta => meta.get(id) ?? { name: id, icon: null },
		[meta]
	)

	const filtered = kinds ? history.rows.filter((r) => kinds.includes(r.kind)) : history.rows
	const rows = limit ? filtered.slice(0, limit) : filtered

	if (!address) {
		return (
			<Empty
				testId="alkanes-history-connect"
				title="Connect to see your history"
				body="Your alkane trades, wraps, mints and transfers — from this browser and from your own on-chain history — appear here."
			/>
		)
	}
	if (history.isLoading && rows.length === 0) {
		return (
			<ul className="divide-y divide-border/40" aria-label="Loading history">
				{[0, 1, 2].map((i) => (
					<li key={i} className="flex items-center gap-3 px-4 py-3">
						<Skeleton className="size-8 shrink-0 rounded-full" />
						<div className="flex-1 space-y-1.5">
							<Skeleton className="h-3.5 w-28" />
							<Skeleton className="h-2.5 w-36" />
						</div>
						<div className="space-y-1.5">
							<Skeleton className="ml-auto h-3.5 w-24" />
							<Skeleton className="ml-auto h-2.5 w-16" />
						</div>
					</li>
				))}
			</ul>
		)
	}
	if (history.unavailable && rows.length === 0) {
		return (
			<Empty
				testId="alkanes-history-unavailable"
				title="History unavailable"
				body="The API did not answer, so this screen cannot claim there is nothing here."
			/>
		)
	}
	if (rows.length === 0) {
		return (
			<Empty
				testId="alkanes-history-empty"
				title={own ? 'Nothing yet' : 'No alkane history'}
				body={
					own
						? 'Your first swap, wrap or mint shows up here the moment you sign it.'
						: 'This wallet has no decoded alkane transactions.'
				}
			/>
		)
	}

	return (
		<div>
			<ul className="divide-y divide-border/40" data-testid="alkanes-history-list">
				{rows.map((r) => (
					<HistoryRowView key={r.txid} row={r} metaOf={metaOf} compact={compact} navigate={navigate} />
				))}
			</ul>
			{(history.more || history.truncated) && (
				<p className="px-4 py-2 text-xxs leading-relaxed text-muted-foreground">
					{history.truncated
						? 'Some history could not be decoded yet — what is shown is a floor, not the whole story.'
						: 'Showing the newest 500 — the wallet’s history runs deeper.'}
				</p>
			)}
		</div>
	)
}

function Empty({ title, body, testId }: { title: string; body: string; testId: string }) {
	return (
		<div className="px-6 py-10 text-center" data-testid={testId}>
			<History className="mx-auto size-7 text-muted-foreground" aria-hidden="true" />
			<span className="mt-2 block text-sm font-medium">{title}</span>
			<span className="mx-auto mt-1 block max-w-[42ch] text-xs leading-relaxed text-muted-foreground">
				{body}
			</span>
		</div>
	)
}

const KIND_LABEL: Record<HistoryRow['kind'], string> = {
	buy: 'Bought',
	sell: 'Sold',
	mint: 'Minted',
	receive: 'Received',
	send: 'Sent',
	lp: 'Liquidity',
	wrap: 'Wrapped',
	unwrap: 'Unwrapped',
	transfer: 'Moved'
}

/// Which way the focus asset moved — the one colour the row spends.
function direction(kind: HistoryRow['kind']): 'in' | 'out' | 'flat' {
	switch (kind) {
		case 'buy':
		case 'mint':
		case 'receive':
		case 'wrap':
			return 'in'
		case 'sell':
		case 'send':
		case 'unwrap':
			return 'out'
		default:
			return 'flat'
	}
}

/// Whole tokens, compact past 100K (fomo's `1.6K 牛来`), exact below — with
/// the full figure on hover either way.
function qty(leg: HistoryLeg): { text: string; full: string } {
	const full = formatAmount(leg.qty, leg.qty < 100_000_000n ? 8 : 4)
	const whole = Number(leg.qty) / 1e8
	return { text: whole >= 100_000 ? numCompact(whole) : full, full }
}

function isBase(id: string): boolean {
	return id === BTC_ID || id === FRBTC_ID
}

/// The leg the row is ABOUT and the leg it traded against. A buy is about
/// what arrived; a sell about what left; a wrap about the frBTC minted.
function legsOf(row: HistoryRow): { focus: HistoryLeg | null; counter: HistoryLeg | null } {
	const dir = direction(row.kind)
	const primary = dir === 'out' ? row.out : row.in
	const secondary = dir === 'out' ? row.in : row.out
	const focus = primary.find((l) => !isBase(l.id)) ?? primary[0] ?? secondary[0] ?? null
	const counter = secondary.find((l) => l.id !== focus?.id) ?? null
	return { focus, counter }
}

function when(row: HistoryRow): string {
	if (!row.ts) return '—'
	const d = new Date(row.ts)
	const sameYear = d.getFullYear() === new Date().getFullYear()
	return d.toLocaleString('en-US', {
		month: 'short',
		day: 'numeric',
		...(sameYear ? {} : { year: 'numeric' }),
		hour: 'numeric',
		minute: '2-digit'
	})
}

function TokenMark({ meta, size }: { meta: TokenMeta; size: number }) {
	const [broken, setBroken] = React.useState(false)
	if (meta.icon && !broken) {
		return (
			<img
				src={meta.icon}
				alt=""
				width={size}
				height={size}
				loading="lazy"
				onError={() => setBroken(true)}
				style={{ width: size, height: size }}
				className="shrink-0 rounded-full object-cover"
			/>
		)
	}
	return (
		<span
			aria-hidden="true"
			style={{ width: size, height: size }}
			className="grid shrink-0 place-items-center rounded-full bg-secondary text-xxs font-bold text-muted-foreground"
		>
			{meta.name.slice(0, 2).toUpperCase()}
		</span>
	)
}

function HistoryRowView({
	row,
	metaOf,
	compact,
	navigate
}: {
	row: HistoryRow
	metaOf: (id: string) => TokenMeta
	compact: boolean
	navigate: (view: 'home', assetId: string) => void
}) {
	const { focus, counter } = legsOf(row)
	const dir = direction(row.kind)
	const focusMeta = focus ? metaOf(focus.id) : null
	const counterMeta = counter ? metaOf(counter.id) : null
	// Only a tradable token opens a market; BTC and frBTC have none.
	const target = focus && !isBase(focus.id) ? focus.id : null
	const focusQty = focus ? qty(focus) : null
	const counterQty = counter ? qty(counter) : null

	const main = (
		<React.Fragment>
			{focusMeta ? (
				<TokenMark meta={focusMeta} size={32} />
			) : (
				<span className="grid size-8 shrink-0 place-items-center rounded-full bg-secondary">
					<History className="size-3.5 text-muted-foreground" aria-hidden="true" />
				</span>
			)}
			<span className="min-w-0 flex-1">
				<span className="block truncate text-sm font-semibold leading-tight">
					{focusMeta?.name ?? 'Alkanes'}
				</span>
				<span className="mt-0.5 flex items-center gap-1.5 text-xxs text-muted-foreground">
					<span
						className={cn(
							'font-semibold',
							dir === 'in' && 'text-success-ink',
							dir === 'out' && 'text-destructive-ink'
						)}
					>
						{KIND_LABEL[row.kind]}
					</span>
					<span aria-hidden="true">·</span>
					{row.pending ? (
						<span
							data-testid="alkanes-history-pending"
							className="inline-flex items-center gap-1 text-warning-ink"
						>
							<span
								aria-hidden="true"
								className="size-1.5 rounded-full bg-warning motion-safe:animate-pulse"
							/>
							{row.source === 'local' ? 'Signed · relaying' : 'In mempool'}
						</span>
					) : (
						<span className="tabular-nums">{when(row)}</span>
					)}
				</span>
			</span>
			<span className="shrink-0 text-right tabular-nums">
				{focusQty ? (
					<span
						title={`${focusQty.full} ${focusMeta?.name ?? ''}`}
						className={cn(
							'block text-sm font-semibold leading-tight',
							dir === 'in' && 'text-success-ink',
							dir === 'out' && 'text-destructive-ink'
						)}
					>
						{dir === 'out' ? '−' : dir === 'in' ? '+' : ''}
						{row.floor && dir === 'in' ? '≥' : ''}
						{focusQty.text} {focusMeta?.name}
					</span>
				) : (
					<span className="block text-sm text-muted-foreground">—</span>
				)}
				{counterQty && (
					<span
						title={`${counterQty.full} ${counterMeta?.name ?? ''}`}
						className="mt-0.5 block text-xxs text-muted-foreground"
					>
						{dir === 'out' ? 'for ' : 'with '}
						{row.floor && dir === 'out' ? '≥' : ''}
						{counterQty.text} {counterMeta?.name}
					</span>
				)}
			</span>
		</React.Fragment>
	)

	return (
		<li
			data-testid={`alkanes-history-${row.kind}`}
			className={cn('flex items-center', compact ? 'gap-2 pl-3 pr-1.5' : 'gap-3 pl-4 pr-2')}
		>
			{target ? (
				<button
					type="button"
					onClick={() => navigate('home', target)}
					aria-label={`Open ${focusMeta?.name ?? target}`}
					className={cn(
						'-ml-1.5 flex min-w-0 flex-1 items-center rounded-lg pl-1.5 text-left transition-colors hover:bg-secondary/40',
						compact ? 'gap-2.5 py-2' : 'gap-3 py-2.5'
					)}
				>
					{main}
				</button>
			) : (
				<div
					className={cn('flex min-w-0 flex-1 items-center', compact ? 'gap-2.5 py-2' : 'gap-3 py-2.5')}
				>
					{main}
				</div>
			)}
			{/* The explorer door, OUTSIDE the market button. */}
			<a
				href={`${ESPO_URL}/tx/${row.txid}`}
				target="_blank"
				rel="noreferrer"
				title={`View ${row.txid.slice(0, 8)}… on the explorer`}
				className="grid size-7 shrink-0 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
			>
				<ExternalLink className="size-3" aria-hidden="true" />
				<span className="sr-only">View transaction on the explorer</span>
			</a>
		</li>
	)
}
